import { Chat } from '@mint-sdk/api-ai-assistant';

export type ReportMetadata =
  Chat.AiChatMessageRecordType['message']['additionalMetadata']['metadata'];

export type ResolvedReport = {
  /** Path handed to `Chat.getReport` to fetch the bytes. */
  filePath: string;
  /** PDF rendition when the backend produced one — drives the preview modal. */
  pdfPath: string | null;
  /** Direct link when the backend supplies one. Null on legacy payloads. */
  uri: string | null;
};

/**
 * Single reader for the report download.
 *
 * The backend sends two shapes and both are live at once — a chat history can
 * contain messages of either kind, so this reconciles them rather than picking
 * one. Legacy messages keep `toDownload` / `pdfRequired` / `pdfS3Path` flat on
 * `metadata` and carry only `{ s3Path, url }` under `report`; current ones nest
 * all of it inside `report` and add `uri`.
 *
 * Deliberately reads plain optional fields rather than normalising up front, so
 * it works identically on a validated history response and on a raw socket
 * message (which never passes through Zod). See ChatSchema.ts for the contract.
 */
export const resolveReportDownload = (
  metadata: ReportMetadata,
): ResolvedReport | null => {
  const report = metadata?.report;

  /* LEGACY COMPAT: once no legacy payloads remain, drop
     the five lines below to `const { toDownload, pdfRequired, pdfS3Path, uri }
     = report ?? {}` plus `const s3Path = report?.s3Path`. ─── */
  const toDownload = report?.toDownload ?? metadata?.toDownload;
  const pdfRequired = report?.pdfRequired ?? metadata?.pdfRequired;
  const pdfS3Path = report?.pdfS3Path ?? metadata?.pdfS3Path;
  const s3Path = report?.s3Path ?? metadata?.objectS3Path;
  const uri = report?.uri ?? report?.url;
  /* ─── END LEGACY COMPAT ─── */

  if (!toDownload) {
    return null;
  }

  if (pdfRequired && pdfS3Path) {
    return {
      filePath: pdfS3Path,
      pdfPath: pdfS3Path,
      uri: uri ?? null,
    };
  }

  const filePath = s3Path ?? pdfS3Path;

  if (!filePath) {
    return null;
  }

  return {
    filePath,
    pdfPath: pdfS3Path ?? null,
    uri: uri ?? null,
  };
};
